import { type SortOptions, sortOptions } from './sort-products';

/** Query param name for the current PLP page — shared by categories,
 * collections and `/search` so the middleware rewrite (`src/proxy.ts`) and
 * `generateMetadata` on every PLP route agree on one name. */
const PAGE_PARAM = 'page';

/** Parses the `page` query param, defaulting to `1` for anything missing,
 * non-numeric, non-integer, or less than `1`. */
export function parsePageParam(value: string | undefined | null): number {
  if (!value) return 1;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return 1;
  return parsed;
}

const VALID_SORT_VALUES = new Set<string>(sortOptions.map((o) => o.value));

/** Parses the `sortBy` query param, defaulting to `DEFAULT_PLP_SORT` for
 * anything that isn't a known sort value — an unvalidated cast would let
 * `?sortBy=garbage` flow into fetch branching and pagination links. */
export function parseSortParam(value: string | undefined | null): SortOptions {
  return value && VALID_SORT_VALUES.has(value)
    ? (value as SortOptions)
    : DEFAULT_PLP_SORT;
}

/** Parses a `minPrice`/`maxPrice` query param. Rejects anything non-numeric
 * or negative (`NaN` would otherwise propagate into price filtering and into
 * pagination hrefs as `minPrice=NaN`). `0` is a valid bound. */
export function parsePriceParam(
  value: string | undefined | null,
): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return undefined;
  return parsed;
}

export function computeTotalPages(
  totalItems: number,
  pageSize: number,
): number {
  if (totalItems <= 0 || pageSize <= 0) return 1;
  return Math.ceil(totalItems / pageSize);
}

/** Builds the href for page `page`, preserving every other query param
 * (`sortBy`/`minPrice`/`maxPrice`) already present. Page 1 omits the `page`
 * param entirely so the base URL stays the canonical, no-query-string form. */
export function buildPageHref(
  pathname: string,
  page: number,
  currentSearchParams: URLSearchParams,
): string {
  const params = new URLSearchParams(currentSearchParams);
  if (page <= 1) {
    params.delete(PAGE_PARAM);
  } else {
    params.set(PAGE_PARAM, String(page));
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/** The sort every PLP renders when no `sortBy` param is present. */
export const DEFAULT_PLP_SORT: SortOptions = 'popular';

/** Query params (`sortBy`/`minPrice`/`maxPrice`/`q`) for pagination hrefs and
 * `rel=next`/`rel=prev` links. The default sort is omitted so linked
 * pagination URLs stay in their clean, self-canonical `?page=N` form — the
 * dynamic PLP routes default a missing `sortBy` to `popular` anyway
 * (see `parsePlpListingParams`). */
export function buildPlpLinkParams({
  sortBy,
  minPrice,
  maxPrice,
  searchQuery,
}: {
  sortBy: SortOptions;
  minPrice?: number;
  maxPrice?: number;
  searchQuery?: string;
}): URLSearchParams {
  const params = new URLSearchParams();
  if (sortBy !== DEFAULT_PLP_SORT) params.set('sortBy', sortBy);
  if (minPrice != null) params.set('minPrice', String(minPrice));
  if (maxPrice != null) params.set('maxPrice', String(maxPrice));
  if (searchQuery) params.set('q', searchQuery);
  return params;
}

export type PlpSearchParamsInput = {
  page?: string;
  sortBy?: string;
  minPrice?: string;
  maxPrice?: string;
};

export type PlpListingParams = {
  page: number;
  sortBy: SortOptions;
  minPrice?: number;
  maxPrice?: number;
  hasFilterOrSort: boolean;
};

/** Parses the raw string searchParams shared by every dynamic PLP route
 * (categories-paginated, collections-paginated, `/search`) into typed
 * listing params, plus whether any filter/sort is applied at all (drives
 * the `noindex` decision — see `buildPlpRobots`). `hasFilterOrSort` reflects
 * raw param *presence*, not validity: a junk `?sortBy=garbage` URL still
 * renders as `noindex` rather than becoming an indexable duplicate. */
export function parsePlpListingParams(
  searchParams: PlpSearchParamsInput,
): PlpListingParams {
  return {
    page: parsePageParam(searchParams.page),
    sortBy: parseSortParam(searchParams.sortBy),
    minPrice: parsePriceParam(searchParams.minPrice),
    maxPrice: parsePriceParam(searchParams.maxPrice),
    hasFilterOrSort:
      searchParams.sortBy !== undefined ||
      searchParams.minPrice !== undefined ||
      searchParams.maxPrice !== undefined,
  };
}
